/**
 * @fileoverview RBAC API Service
 * @description Client-side service for role-based access control operations.
 * @module services/api/rbac
 */

import { apiClient } from './client';
import { API_ENDPOINTS } from './endpoints';

/**
 * Fetch current user's roles and module permissions.
 * @returns {Promise<{roles: string[], permissions: Object, is_admin: boolean}>}
 */
export async function fetchMyPermissions() {
  const response = await apiClient.get(API_ENDPOINTS.RBAC.MY_PERMISSIONS);
  return response?.data ?? response;
}

/**
 * Fetch all available roles.
 * @param {boolean} [includeInactive=false]
 * @returns {Promise<Array>}
 */
export async function fetchRoles(includeInactive = false) {
  const params = includeInactive ? { include_inactive: 'true' } : {};
  const response = await apiClient.get(API_ENDPOINTS.RBAC.ROLES, params);
  return response?.data?.items ?? response?.data ?? response;
}

/**
 * Create a new role (admin only).
 * @param {Object} data - { name, display_name, description, is_default }
 * @returns {Promise<Object>}
 */
export async function createRole(data) {
  const response = await apiClient.post(API_ENDPOINTS.RBAC.ROLES, data);
  return response?.data ?? response;
}

/**
 * Update a role (admin only).
 * @param {number} roleId
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function updateRole(roleId, data) {
  const response = await apiClient.put(API_ENDPOINTS.RBAC.ROLE_BY_ID(roleId), data);
  return response?.data ?? response;
}

/**
 * Delete (deactivate) a role (admin only).
 * @param {number} roleId
 * @returns {Promise<void>}
 */
export async function deleteRole(roleId) {
  return apiClient.delete(API_ENDPOINTS.RBAC.ROLE_BY_ID(roleId));
}

/**
 * Fetch all modules.
 * @returns {Promise<Array>}
 */
export async function fetchModules() {
  const response = await apiClient.get(API_ENDPOINTS.RBAC.MODULES);
  return response?.data?.items ?? response?.data ?? response;
}

/**
 * Get permissions for a specific role.
 * @param {number} roleId
 * @returns {Promise<Array>}
 */
export async function fetchRolePermissions(roleId) {
  const response = await apiClient.get(API_ENDPOINTS.RBAC.ROLE_PERMISSIONS(roleId));
  return response?.data?.items ?? response?.data ?? response;
}

/**
 * Set permissions for a role (admin only).
 * @param {number} roleId
 * @param {number[]} permissionIds
 * @returns {Promise<Array>}
 */
export async function setRolePermissions(roleId, permissionIds) {
  const response = await apiClient.put(API_ENDPOINTS.RBAC.ROLE_PERMISSIONS(roleId), {
    permission_ids: permissionIds,
  });
  return response?.data?.items ?? response?.data ?? response;
}

/**
 * Get module permissions.
 * @param {number} moduleId
 * @returns {Promise<Array>}
 */
export async function fetchModulePermissions(moduleId) {
  const response = await apiClient.get(API_ENDPOINTS.RBAC.MODULE_PERMISSIONS(moduleId));
  return response?.data?.items ?? response?.data ?? response;
}

/**
 * Get a user's roles (admin only).
 * @param {string} userId - The user's subject ID (e.g. 'v-smondal1')
 * @returns {Promise<Array>}
 */
export async function fetchUserRoles(userId) {
  const response = await apiClient.get(API_ENDPOINTS.RBAC.USER_ROLES(userId));
  return response?.data?.items ?? response?.data ?? response;
}

/**
 * Assign a role to a user (admin only).
 * @param {string} userId - The user's subject ID
 * @param {number} roleId
 * @returns {Promise<Array>}
 */
export async function assignUserRole(userId, roleId) {
  const response = await apiClient.post(API_ENDPOINTS.RBAC.USER_ROLES(userId), {
    role_id: roleId,
  });
  return response?.data?.items ?? response?.data ?? response;
}

/**
 * Revoke a role from a user (admin only).
 * @param {string} userId - The user's subject ID
 * @param {number} roleId
 * @returns {Promise<void>}
 */
export async function revokeUserRole(userId, roleId) {
  return apiClient.delete(API_ENDPOINTS.RBAC.REVOKE_USER_ROLE(userId, roleId));
}

/**
 * Submit an access request.
 * @param {Object} data - { module_id, requested_role_id?, reason? }
 * @returns {Promise<Object>}
 */
export async function submitAccessRequest(data) {
  const response = await apiClient.post(API_ENDPOINTS.RBAC.ACCESS_REQUESTS, data);
  return response?.data ?? response;
}

/**
 * Get all access requests (admin only).
 * @param {string} [status] - Filter by status: pending, approved, rejected
 * @returns {Promise<Array>}
 */
export async function fetchAccessRequests(status) {
  const params = status ? { status } : {};
  const response = await apiClient.get(API_ENDPOINTS.RBAC.ACCESS_REQUESTS, params);
  return response?.data?.items ?? response?.data ?? response;
}

/**
 * Get current user's access requests.
 * @returns {Promise<Array>}
 */
export async function fetchMyAccessRequests() {
  const response = await apiClient.get(API_ENDPOINTS.RBAC.MY_ACCESS_REQUESTS);
  return response?.data?.items ?? response?.data ?? response;
}

/**
 * Approve or reject an access request (admin only).
 * @param {number} requestId
 * @param {string} status - 'approved' or 'rejected'
 * @param {string} [reviewNotes]
 * @returns {Promise<Object>}
 */
export async function reviewAccessRequest(requestId, status, reviewNotes) {
  const response = await apiClient.patch(
    API_ENDPOINTS.RBAC.REVIEW_ACCESS_REQUEST(requestId),
    { status, review_notes: reviewNotes }
  );
  return response?.data ?? response;
}

// ── User Module Access (admin) ──

export async function fetchUserModules(userId) {
  const response = await apiClient.get(API_ENDPOINTS.RBAC.USER_MODULES(userId));
  return response?.data?.items ?? response?.data ?? response;
}

export async function grantUserModule(userId, moduleId) {
  const response = await apiClient.post(API_ENDPOINTS.RBAC.USER_MODULES(userId), {
    module_id: moduleId,
  });
  return response?.data?.items ?? response?.data ?? response;
}

export async function revokeUserModule(userId, moduleId) {
  return apiClient.delete(API_ENDPOINTS.RBAC.REVOKE_USER_MODULE(userId, moduleId));
}

export const rbacApi = {
  fetchMyPermissions,
  fetchRoles,
  createRole,
  updateRole,
  deleteRole,
  fetchModules,
  fetchRolePermissions,
  setRolePermissions,
  fetchModulePermissions,
  fetchUserRoles,
  assignUserRole,
  revokeUserRole,
  submitAccessRequest,
  fetchAccessRequests,
  fetchMyAccessRequests,
  reviewAccessRequest,
  fetchUserModules,
  grantUserModule,
  revokeUserModule,
};
